import React from 'react'
import styled from '@emotion/styled'
import { RacePageDataTransferObject } from '@mobi/api-types/src/racePage/types'
import { Icon } from '@mobi/component-library/Assets/Icon'
import { colors, spacing } from '@mobi/component-library/Foundations'
import { TabButton, Tabs } from '@mobi/component-library/Navigation/Tabs'
import { AppRoutes } from '@core/AppRoutes'
import * as EventAggregator from '@core/Areas/RaceCard/Components/RaceCardContent/Components/LegacyKnockout/ViewModels/AppUtils/Framework/Messaging/EventAggregator'
import { getCurrentBetType } from '@core/Areas/RaceCard/Store/selectors'
import { MORE_BETTING_OPTIONS_ID } from '@core/Areas/RaceCard/constants'
import { CHANGE_BET_TYPE_EVENT } from '@core/Areas/RaceCard/constants'
import { useAppSelector } from '@core/Store/hooks'
import { trackBetTypeChange } from './helpers/analytics'
import { getAvailableBetTypesFromData } from './helpers/getAvailableBetTypesFromData'

const enum LocalConstants {
  SkyRaceCardClassname = 'js-bet-type-selector--sky-race',
}

export const BetTypeSelector: React.FC<{
  raceData: RacePageDataTransferObject
}> = ({ raceData }) => {
  const timerRef = React.useRef<number>(undefined)

  const selectedBetType = useAppSelector(getCurrentBetType)

  const availableBetTypes = getAvailableBetTypesFromData(raceData)

  const createBetTypeClickHandler =
    ({ betType, name }: BetTypeObject) =>
    () => {
      if (betType === selectedBetType) return
      EventAggregator.publish(CHANGE_BET_TYPE_EVENT, { betType })
      trackBetTypeChange({ betType, productName: name })
    }

  const onMoreOptionsClick = () => {
    trackBetTypeChange({ betType: 'WinPlace', productName: 'More Betting Options' })
    if (timerRef.current) clearTimeout(timerRef.current)
    if (selectedBetType === 'WinPlace') {
      const moreBettingOptionsEl = document.getElementById(MORE_BETTING_OPTIONS_ID)
      moreBettingOptionsEl?.scrollIntoView({ behavior: 'smooth' })
    } else {
      const domObserver = new MutationObserver((_, observer) => {
        const moreBettingOptionsEl = document.getElementById(MORE_BETTING_OPTIONS_ID)
        if (!moreBettingOptionsEl) return
        timerRef.current = window.setTimeout(() => {
          moreBettingOptionsEl.scrollIntoView({ behavior: 'smooth' })
        }, 1_000)
        observer.disconnect()
      })
      domObserver.observe(document.body, { childList: true, subtree: true })
      EventAggregator.publish(CHANGE_BET_TYPE_EVENT, { betType: 'WinPlace' })
    }
  }

  React.useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [])

  const isOnSkyRacePage = window.location.pathname === AppRoutes.RaceCardSky

  return (
    <WrapperStyled
      data-js-race-card-no-swipe
      data-testid='BetTypeSelector'
      className={isOnSkyRacePage ? (LocalConstants.SkyRaceCardClassname as string) : ''}
    >
      <Tabs>
        {availableBetTypes.map(betType => (
          <TabButton
            key={betType.betType}
            testId={`bet-type-option-${betType.name.toLowerCase().replaceAll(' ', '-')}`}
            content={
              <>
                {betType.hasJackpot && (
                  <span data-testid='BetTypeSelector.Jackpot'>
                    <Icon
                      name='DuocolorCurrencyDollarCircle'
                      color={colors.orange[500]}
                      size='1.8rem'
                    />
                  </span>
                )}

                {betType.name}
              </>
            }
            isSelected={betType.betType === selectedBetType}
            onClick={createBetTypeClickHandler(betType)}
          />
        ))}

        <TabButton
          content='More Betting Options'
          isSelected={false}
          onClick={onMoreOptionsClick}
          testId='bet-type-option-more-betting-options'
        />
      </Tabs>
    </WrapperStyled>
  )
}

// ======
// Styles
// ======

const WrapperStyled = styled.div({
  [`&.${LocalConstants.SkyRaceCardClassname} ul`]: {
    paddingLeft: spacing.smx1,
  },
})

// =====
// Types
// =====

type BetTypeObject = ReturnType<typeof getAvailableBetTypesFromData>[0]
